import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Cpu, GitMerge, ShieldCheck, Users, Zap, LifeBuoy } from 'lucide-react';
const focuses = [{
  icon: Cpu,
  title: 'Machine-Enabled Intelligence',
  description: 'Turning AI from a buzzword into measurable strategic leverage for your leadership team.',
  href: '/domain-focus#focus-machine-intelligence'
}, {
  icon: GitMerge,
  title: 'Post-Merger Integration',
  description: 'Capturing the value the deal promised — across people, systems, and operating models.',
  href: '/domain-focus#focus-pmi'
}, {
  icon: ShieldCheck,
  title: 'Governance & Accountability',
  description: 'Reshaping how decisions are made, owned, and measured at the executive level.',
  href: '/domain-focus#focus-governance'
}, {
  icon: Users,
  title: 'Leadership Cohesion',
  description: 'Bridging cultural fault-lines so leadership teams move as one — not as factions.',
  href: '/domain-focus#focus-cohesion'
}, {
  icon: Zap,
  title: 'Executive Velocity',
  description: 'Compressing the distance between strategic intent and operational reality.',
  href: '/domain-focus#focus-velocity'
}, {
  icon: LifeBuoy,
  title: 'Program Rescue & Reset',
  description: 'Recovering value from initiatives that have stalled, drifted, or lost their way.',
  href: '/domain-focus#focus-rescue'
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.07
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 14
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut'
    }
  }
};
export function DomainFocus() {
  return <section className="py-20 md:py-28 border-t border-white/5" data-id="element-27">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-28">
        <div className="mb-12 md:mb-16 max-w-4xl" data-id="element-29">
          <p className="text-sm font-medium tracking-wider uppercase text-amber mb-4" data-id="element-30">
            Our Domain Focus
          </p>
          <h2 className="text-3xl md:text-5xl font-serif text-offwhite leading-tight" data-id="element-31">
            Transform your Inflection Point from a Liability into a{' '}
            <span className="italic text-amber" data-id="element-32">Launchpad</span>.
          </h2>
        </div>

        <motion.ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-80px'
      }} data-id="element-33">
          {focuses.map(focus => {
          const Icon = focus.icon;
          return <motion.li key={focus.title} variants={itemVariants} data-id="element-34">
                <Link to={focus.href} className="group relative flex flex-col h-full p-6 md:p-7 bg-navy-light/40 border border-white/10 rounded-md hover:border-amber/50 hover:bg-navy-light transition-all duration-300 overflow-hidden" data-id="element-35">
                  {/* Subtle hover glow */}
                  <div className="absolute -top-px -left-px h-px w-0 bg-amber group-hover:w-full transition-all duration-500" data-id="element-36" />

                  <div className="flex items-center gap-3 mb-3" data-id="element-37">
                    <Icon size={20} className="text-amber flex-shrink-0" strokeWidth={1.75} data-id="element-38" />
                    <h3 className="text-lg md:text-xl font-serif text-offwhite leading-snug group-hover:text-amber transition-colors" data-id="element-39">
                      {focus.title}
                    </h3>
                  </div>

                  <p className="text-sm md:text-[0.95rem] text-offwhite-muted font-light leading-relaxed" data-id="element-40">
                    {focus.description}
                  </p>

                  <div className="mt-5 flex items-center gap-1.5 text-xs font-medium tracking-wider uppercase text-offwhite-muted group-hover:text-amber transition-colors" data-id="element-41">
                    Learn more
                    <ArrowUpRight size={14} className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" data-id="element-42" />
                  </div>
                </Link>
              </motion.li>;
        })}
        </motion.ul>
      </div>
    </section>;
}